import { TICKETS_ANCHORS, TICKETS_CONTEXTS } from './anchors'
import { SHARED_ANCHORS } from '../shared/anchors'
import { earsSteps, taskCardSteps } from '../shared/steps'
import { withScopes } from '../shared/scopes'
import type { IOnboardingScenario } from '../../types/IOnboardingScenario'
import type { IOnboardingScope } from '../../types/IOnboardingScope'
import type { IOnboardingStep } from '../../types/IOnboardingStep'

const { table, categories, workGroups, form } = TICKETS_ANCHORS
const ctx = TICKETS_CONTEXTS

/*
 * Области таблицы:
 * - actionsMenu — раскрытое «Меню действий» на мобильном (на десктопе действие ничего не делает);
 * - settingsMenu — открытое меню «Настройки»;
 * - categories — модалка категорий заявок;
 * - workGroups — страница групп исполнителей.
 */
const tableScopes: Record<string, IOnboardingScope> = {
  actionsMenu: { open: { context: ctx.table, action: 'openActionsMenu' } },
  settingsMenu: {
    open: { context: ctx.table, action: 'openSettingsMenu' },
    close: { context: ctx.table, action: 'closeSettingsMenu' },
  },
  categories: {
    open: { context: ctx.table, action: 'openCategories' },
    close: { context: ctx.table, action: 'closeCategories' },
  },
  workGroups: {
    open: { context: ctx.table, action: 'openWorkGroups' },
    close: { context: ctx.workGroups, action: 'backToTickets' },
  },
}

const tableSteps: IOnboardingStep[] = [
  { id: 'tickets.intro', anchors: [], context: ctx.table },
  { id: 'tickets.create', anchors: [table.create], context: ctx.table },
  ...withScopes(['actionsMenu'], [
    { id: 'tickets.statistics', anchors: [table.statistics], context: ctx.table },
    { id: 'tickets.reports', anchors: [table.reports], context: ctx.table },
  ]),
  ...withScopes(['actionsMenu', 'settingsMenu'], [
    { id: 'tickets.settings', anchors: [table.settings, table.settingsCategories], context: ctx.table, condition: 'tickets.canSettings', placement: 'left' },
  ]),
  { id: 'tickets.navSettings', anchors: [TICKETS_ANCHORS.nav.settings], context: ctx.table, condition: 'tickets.canSettings', placement: 'right' },
  ...withScopes(['categories'], [
    { id: 'tickets.categories.tree', anchors: [categories.tree], context: ctx.table, condition: 'tickets.canEditCategories', placement: 'auto' },
    { id: 'tickets.categories.filter', anchors: [categories.filter], context: ctx.table, condition: 'tickets.canEditCategories' },
    { id: 'tickets.categories.view', anchors: [categories.view, categories.viewLast], context: ctx.table, condition: 'tickets.canEditCategories' },
    { id: 'tickets.categories.create', anchors: [categories.create], context: ctx.table, condition: 'tickets.canEditCategories' },
  ]),
  ...withScopes(['workGroups'], [
    { id: 'tickets.workGroups.table', anchors: [workGroups.table], context: ctx.workGroups, condition: 'tickets.canEditWorkGroups', placement: 'auto' },
    { id: 'tickets.workGroups.add', anchors: [workGroups.add], context: ctx.workGroups, condition: 'tickets.canEditWorkGroups' },
  ]),
  ...withScopes(['actionsMenu'], [
    { id: 'tickets.export', anchors: [table.export], context: ctx.table },
    { id: 'toolbar.reports', anchors: [SHARED_ANCHORS.toolbar.reports], context: ctx.table },
    { id: 'toolbar.onboarding', anchors: [SHARED_ANCHORS.toolbar.onboarding], context: ctx.table },
  ]),
  { id: 'tickets.search', anchors: [table.search], context: ctx.table },
  { id: 'tickets.requiresAction', anchors: [table.requiresAction], context: ctx.table },
  { id: 'tickets.filters', anchors: [table.filterFirst, table.filterLast], context: ctx.table },
  { id: 'tickets.resetFilters', anchors: [table.resetFilters], context: ctx.table },
  { id: 'tickets.hideCompleted', anchors: [table.hideCompleted], context: ctx.table },
  { id: 'tickets.demoRow', anchors: [table.demoRow], context: ctx.table },
]

/* Области формы одинаковы в обоих сценариях, отличается только то, как открывается сама форма */
const formSteps: IOnboardingStep[] = [
  ...withScopes(['form'], [
    { id: 'tickets.form.serviceCategory', anchors: [form.serviceCategory], context: ctx.form },
    { id: 'tickets.form.departmentLocation', anchors: [form.department, form.location], context: ctx.form },
    { id: 'tickets.form.files', anchors: [form.files], context: ctx.form },
    { id: 'tickets.form.assignSelf', anchors: [form.assignSelf], context: ctx.form },
    { id: 'tickets.form.save', anchors: [form.save], context: ctx.form },
    { id: 'tickets.form.historyTab', anchors: [form.historyTab], context: ctx.form },
    { id: 'tickets.form.tasksEar', anchors: [SHARED_ANCHORS.ear.tasks], context: ctx.form, placement: 'left' },
  ]),
  ...withScopes(['form', 'tasksPane'], [
    { id: 'tickets.form.assignCoordinator', anchors: [form.assignCoordinator], context: ctx.formTasks, placement: 'left' },
    ...taskCardSteps(ctx.formTasks),
  ]),
  ...earsSteps(ctx.form, ['form']),
]

const tasksPaneScope: IOnboardingScope = {
  open: { context: ctx.formTasks, action: 'openTasksPane' },
  close: { context: ctx.formTasks, action: 'closeTasksPane' },
}

const chatPaneScope: IOnboardingScope = {
  open: { context: ctx.form, action: 'openDemoChat' },
  close: { context: ctx.form, action: 'closeDemoChat' },
}

export const ticketsScenarios: IOnboardingScenario[] = [
  {
    id: 'tickets',
    section: 'ticket',
    isMain: true,
    scopes: {
      ...tableScopes,
      form: {
        open: { context: ctx.table, action: 'openDemoForm' },
        close: { context: ctx.table, action: 'closeForm' },
      },
      tasksPane: tasksPaneScope,
      chatPane: chatPaneScope,
    },
    steps: [...tableSteps, ...formSteps],
  },
  {
    id: 'tickets.form',
    section: 'ticket',
    isMain: false,
    scopes: {
      form: {
        open: { context: ctx.form, action: 'showDemo' },
        close: { context: ctx.form, action: 'showReal' },
      },
      tasksPane: tasksPaneScope,
      chatPane: chatPaneScope,
    },
    steps: formSteps,
  },
]
